import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Col, FormControl, Row } from 'react-bootstrap';
import CurrencyInput from '../components/common/CurrencyInput';
import CustomMultiSelect from '../components/common/CustomMultiSelect';
import ExplainatoryVideoButton from '../components/common/ExplainatoryVideoButton';
import HorizontalProgress from '../components/common/HorizontalProgress';
import RangeInput from '../components/common/RangeInput';
import SwitchInput from '../components/common/SwitchInput';
import { ApartmentProfileFields, dealStatusToColor } from '../helpers/constants';
import { apartmentTypes, isNullOrUndefined, apartmentStatuses } from '../helpers/global';
import useLocalization from '../hooks/useLocalization';
import { snakeCase } from 'lodash';
import MultiCheckboxInput from '../components/common/MultiCheckboxInput';
import { Calendar2Date } from 'react-bootstrap-icons';
import { getSortedCities } from '../helpers/constants';
import useAuth from '../hooks/useAuth';
import { isCustomer } from '../helpers/global';
import { LockIcon } from 'lucide-react';

const cities = getSortedCities();

const getApartmentName = apartment => {
  return [apartment.gushTatHelka, apartment.type, apartment.address].filter(Boolean).join('-');
};

const CustomerProfileForm = ({
  editMode,
  values,
  onSubmit,
  onDeleteClick,
  onToggleApproval,
  onPublishForReview,
  showProgress,
  relatedDeals,
  fromCustomerPortal,
  readOnly = false
}) => {
  const { user } = useAuth();
  const { translate, isRTL } = useLocalization();
  const [data, setData] = useState({});
  const [errors, setErrors] = useState({});

  const isProfileLocked =
    fromCustomerPortal && (data?.profileEditLocked || (data?.isApproved && data?.isPublishedForReview));

  // disabled profile update if not published for review in admin view
  const isUnpublisedCustomerProfileInAdminView =
    !fromCustomerPortal && editMode && !data?.isPublishedForReview && data?.showToCustomer;

  const fieldsAccordingToApartmentType = useMemo(() => {
    return ApartmentProfileFields[data?.type];
  }, [data]);

  const handleChange = ({ fieldType, key, objectKey, value }) => {
    if (fieldType === 'minMax' || objectKey) {
      setData({
        ...data,
        [key]: {
          ...(data[key] || {}),
          [objectKey]: value
        }
      });
      return;
    }

    setData({
      ...data,
      [key]: value
    });
  };

  const handleValidation = () => {
    const newErrors = {};
    fieldsAccordingToApartmentType?.forEach(field => {
      if (field.type === 'minMax') {
        const min = data[field.key]?.min;
        const max = data[field.key]?.max;
        if (!isNullOrUndefined(max) && min > max) {
          newErrors[field.key] = 'Min value cannot be greater than max value';
        }
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (handleValidation()) {
      onSubmit({ ...data, status: data?.status || 'In Active Search' });
    }
  };

  const handleSaveAndPublish = () => {
    if (handleValidation()) {
      onSubmit({ ...data, status: data?.status || 'In Active Search', isPublishedForReview: true });
    }
  };

  const handlePublishForReview = () => {
    if (onPublishForReview) {
      onPublishForReview(data);
    }
  };

  const handleViewApartment = () => {
    if (data?.linkedApartment) {
      sessionStorage.setItem('apartmentId', data.linkedApartment._id);
      window.open('/apartments', '_blank');
    }
  };

  useEffect(() => {
    if (values) {
      setData({ ...values, ...(!editMode && { city: cities.map(c => c.value) }) });
    }
  }, [values, editMode]);

  const isLA = useMemo(() => {
    return data?.linkedApartment;
  }, [data?.linkedApartment]);

  const dealOptionRenderer = ({ data }) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        direction: isRTL ? 'rtl' : 'ltr'
      }}
      className="px-1 py-0"
    >
      <div>
        <div style={{ fontWeight: 'bold' }}>{data?.description || '-'}</div>
        <div style={{ color: '#666' }}>${data?.price?.toLocaleString() || '-'}</div>
      </div>
      <div>
        <div className="mb-1">
          <Badge size="lg" bg={dealStatusToColor[data?.status]}>
            {translate(snakeCase(data?.status)) || data?.status}
          </Badge>
        </div>
        <div>
          <Calendar2Date />
          <span className="mx-2 pt-1">{new Date(data?.createdAt).toLocaleDateString()}</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-2 d-flex flex-column flex-grow-1" style={{ minHeight: 0, flex: '1 1 0%' }}>
      <div className="flex-grow-1 overflow-auto" style={{ minHeight: 0 }}>
        {!editMode && (
          <div className="mx-1 mb-2">
            <ExplainatoryVideoButton videoKey="how_to_fill_out_a_property_profile" showLabel />
          </div>
        )}
        <div className="mx-1">
          <Row>
            <Col md={`${isLA ? 4 : 6}`} lg={`${isLA ? 4 : 6}`}>
              <h6 className="smallFont fw-bold">{translate('apartment_type')}:</h6>
              <div>
                <CustomMultiSelect
                  selectedItems={data?.type || ''}
                  items={apartmentTypes.slice(1).map(a => ({
                    ...a,
                    label:
                      a?.value === 'pinui-binui'
                        ? translate('pinui_binui_label')
                        : a?.value === 'paper-apartment'
                        ? translate('paper_apartment_label')
                        : translate(snakeCase(a.label))
                  }))}
                  onChange={a => setData({ ...data, type: a, city: cities.map(c => c.value) })}
                  isMulti={false}
                  closeMenuOnSelect
                  height="30px"
                  isDisabled={readOnly || isProfileLocked || isUnpublisedCustomerProfileInAdminView}
                />
              </div>
            </Col>
            <Col md={`${isLA ? 4 : 6}`} lg={`${isLA ? 4 : 6}`}>
              <h6 className="smallFont fw-bold">{translate('status')}:</h6>
              <div>
                <CustomMultiSelect
                  selectedItems={data?.status || 'In Active Search'}
                  items={apartmentStatuses.map(a => ({ ...a, label: translate(snakeCase(a.label)) }))}
                  onChange={a => setData(prev => ({ ...prev, status: a }))}
                  isMulti={false}
                  closeMenuOnSelect
                  height="30px"
                  isDisabled={readOnly || isCustomer(user.role) || isUnpublisedCustomerProfileInAdminView}
                />
              </div>
            </Col>
            {data?.linkedApartment && (
              <Col md={4} lg={4}>
                <h6 className="smallFont fw-bold">{translate('linked_apartment')}:</h6>
                <div>
                  {data?.linkedApartment && (
                    <Button
                      key={data?.linkedApartment?._id}
                      size="sm"
                      className="d-flex justify-content-center align-items-center mx-1"
                      variant="outline-dark"
                      onClick={handleViewApartment}
                      style={{ height: '30px' }}
                    >
                      <h6 className="smallFont mb-0">{getApartmentName(data?.linkedApartment)}</h6>
                    </Button>
                  )}
                </div>
              </Col>
            )}
          </Row>
        </div>
        <div className="border rounded p-0 mt-2 ">
          <Row className="m-0 mb-2">
            {fieldsAccordingToApartmentType?.map(field => {
              const label = translate(field?.label) || field?.label;
              return (
                <>
                  <Col
                    md={{ offset: field?.offsetMD || 0, span: field?.sizeMD || 6 }}
                    className={`mt-1 py-1 ${
                      field?.type === 'switch' ? 'd-flex align-items-end justify-content-between gap-2 my-1' : ''
                    } ${field?.className || ''}`}
                    key={field.key}
                  >
                    <h6 className={`smallFont ${field?.type === 'switch' ? 'mb-0' : 'mb-2'}`}>
                      {label} {label.endsWith('?') ? '' : ':'}
                    </h6>
                    {field?.type === 'minMax' ? (
                      <div className="d-flex align-items-center gap-2">
                        {(!field?.objectKey || field?.objectKey === 'min') && (
                          <CurrencyInput
                            size="sm"
                            value={data?.[field.key]?.min || ''}
                            onChange={(value, inputValue) =>
                              handleChange({
                                fieldType: field?.type,
                                key: field.key,
                                objectKey: 'min',
                                value: inputValue === '' ? undefined : value
                              })
                            }
                            placeholder={translate('min')}
                            disabled={readOnly || isProfileLocked || isUnpublisedCustomerProfileInAdminView}
                          />
                        )}
                        {!field?.objectKey && <h6 className="smallFont mb-0">-</h6>}
                        {(!field?.objectKey || field?.objectKey === 'max') && (
                          <CurrencyInput
                            size="sm"
                            value={data?.[field.key]?.max || ''}
                            onChange={(value, inputValue) =>
                              handleChange({
                                fieldType: field?.type,
                                key: field.key,
                                objectKey: 'max',
                                value: inputValue === '' ? undefined : value
                              })
                            }
                            placeholder={translate('max')}
                            disabled={readOnly || isProfileLocked || isUnpublisedCustomerProfileInAdminView}
                          />
                        )}
                      </div>
                    ) : field?.type === 'multi-select' ? (
                      <CustomMultiSelect
                        selectedItems={field?.objectKey ? data?.[field.key]?.[field?.objectKey] : data?.[field?.key]}
                        items={field?.options?.map(option => ({
                          label: translate(option.label) || option.label,
                          value: option.value
                        }))}
                        onChange={value => {
                          let sortedValue = value;
                          if (field?.prorityOptions?.length > 0) {
                            sortedValue = value.sort((a, b) => {
                              if (field?.prorityOptions?.includes(a)) {
                                return -1;
                              }
                              return 1;
                            });
                          }
                          handleChange({
                            fieldType: field?.type,
                            key: field.key,
                            objectKey: field?.objectKey,
                            value: sortedValue
                          });
                        }}
                        height="30px"
                        showInputOnNewLine={true}
                        maxToShow={field?.maxToShow || 50}
                        customSelectAll={field?.customSelectAll}
                        maxItemCustomMessage={
                          field?.maxItemCustomMessage
                            ? length =>
                                translate(length === 1 ? 'n_city_selected' : 'n_cities_selected', { count: length })
                            : undefined
                        }
                        isDisabled={readOnly || isProfileLocked || isUnpublisedCustomerProfileInAdminView}
                      />
                    ) : field?.type === 'multi-checkbox' ? (
                      <MultiCheckboxInput
                        options={field?.options}
                        value={field?.objectKey ? data?.[field.key]?.[field?.objectKey] : data?.[field?.key]}
                        onChange={value => {
                          handleChange({
                            fieldType: field?.type,
                            key: field.key,
                            objectKey: field?.objectKey,
                            value
                          });
                        }}
                        labelClassName="smallFont"
                        size="sm"
                        checkboxStyle={null}
                        disabled={readOnly || isProfileLocked || isUnpublisedCustomerProfileInAdminView}
                      />
                    ) : field?.type === 'range' ? (
                      <RangeInput
                        min={field?.min}
                        max={field?.max}
                        step={1}
                        value={field?.objectKey ? data?.[field.key]?.[field?.objectKey] : data?.[field?.key]}
                        onChange={value => {
                          handleChange({
                            fieldType: field?.type,
                            key: field.key,
                            objectKey: field?.objectKey,
                            value
                          });
                        }}
                        disabled={readOnly || isProfileLocked || isUnpublisedCustomerProfileInAdminView}
                      />
                    ) : field?.type === 'switch' ? (
                      <SwitchInput
                        threeState={field?.threeState}
                        neutralValue={field?.neutralValue}
                        falseValue={field?.falseValue}
                        trueValue={field?.trueValue}
                        value={field?.objectKey ? data?.[field.key]?.[field?.objectKey] : data?.[field?.key]}
                        onChange={value => {
                          handleChange({
                            fieldType: field?.type,
                            key: field.key,
                            objectKey: field?.objectKey,
                            value
                          });
                        }}
                        disabled={readOnly || isProfileLocked || isUnpublisedCustomerProfileInAdminView}
                      />
                    ) : (
                      <FormControl
                        size="sm"
                        as={field?.type === 'textarea' ? 'textarea' : 'input'}
                        value={data?.[field.key] || ''}
                        onChange={e =>
                          handleChange({
                            fieldType: field?.type,
                            key: field.key,
                            objectKey: null,
                            value: e.target.value
                          })
                        }
                        disabled={readOnly || isProfileLocked || isUnpublisedCustomerProfileInAdminView}
                      />
                    )}
                    {errors[field.key] && <div className="text-danger smallFont">{errors[field.key]}</div>}
                  </Col>
                  {field?.offsetAfterMD && <Col md={field?.offsetAfterMD} />}
                </>
              );
            })}
            {!fromCustomerPortal && (
              <Col>
                <h6 className={`smallFont mb-2`}>{translate('related_deal')}:</h6>
                <CustomMultiSelect
                  selectedItems={data?.deal || []}
                  items={relatedDeals
                    ?.sort((a, b) => a.createdAt - b.createdAt)
                    ?.map((deal, i) => ({
                      label: [
                        deal.unit?.unit?.description || '',
                        translate(snakeCase(deal.status)) || deal.status || '',
                        `₪${deal.price || ''}`,
                        new Date(deal.createdAt).toLocaleDateString() || ''
                      ]
                        .filter(Boolean)
                        .join(' - '),
                      value: deal?._id,
                      data: {
                        description: deal?.unit?.unit?.description,
                        status: deal?.status,
                        price: deal?.price,
                        createdAt: deal?.createdAt
                      }
                    }))}
                  onChange={value => {
                    handleChange({
                      fieldType: 'multi-select',
                      key: 'deal',
                      objectKey: null,
                      value
                    });
                  }}
                  height="30px"
                  maxToShow={200}
                  isMulti={false}
                  optionRenderer={dealOptionRenderer}
                  placement="top"
                  maxHeight="500px"
                  closeMenuOnSelect={true}
                  isDisabled={readOnly || isUnpublisedCustomerProfileInAdminView}
                  noOptionsMessage={translate('no_related_deals')}
                />
              </Col>
            )}
          </Row>
        </div>
        {isProfileLocked && (
          <div className="alert alert-warning d-flex align-items-center mt-3 mb-2 py-2" role="alert">
            <LockIcon className="h-4 w-4 me-2"></LockIcon>
            <span className="smallFont fw-bold">
              {data?.isApproved && data?.isPublishedForReview && translate('profile_approved_cannot_updated')}
            </span>
          </div>
        )}
        {isUnpublisedCustomerProfileInAdminView && (
          <div className="alert alert-warning d-flex align-items-center mt-3 mb-2 py-2" role="alert">
            <LockIcon className="h-4 w-4 me-2"></LockIcon>
            <span className="smallFont fw-bold">{translate('profile_not_submitted_for_approval')}</span>
          </div>
        )}
      </div>
      {showProgress && (
        <div className="flex-shrink-0 mx-1 mt-2">
          <HorizontalProgress text={translate('please_wait')} />
        </div>
      )}
      {!readOnly && (
        <div className="border-top d-flex justify-content-between align-items-center pt-2 px-1 pb-2 flex-shrink-0 bg-white">
          <div className="d-flex gap-2">
            {editMode && !fromCustomerPortal && data?.isPublishedForReview && !data?.isApproved && (
              <Button size="sm" variant="outline-success" onClick={() => onToggleApproval(data, true)}>
                <h6 className="mb-0 smallFont">{translate('approve')}</h6>
              </Button>
            )}
          </div>
          <div className="d-flex gap-2">
            {editMode && !isProfileLocked && (
              <Button
                size="sm"
                variant="danger text-white"
                disabled={isProfileLocked || isUnpublisedCustomerProfileInAdminView}
                onClick={() => onDeleteClick(data)}
              >
                <h6 className="mb-0 smallFont">{translate('delete')}</h6>
              </Button>
            )}
            {isCustomer(user.role) && !editMode && (
              <Button
                size="sm"
                variant="primary text-white"
                onClick={handleSaveAndPublish}
                disabled={isProfileLocked || isUnpublisedCustomerProfileInAdminView}
              >
                <h6 className="mb-0 smallFont">{translate('save_and_publish_for_review')}</h6>
              </Button>
            )}
            {isCustomer(user.role) && editMode && (
              <Button
                size="sm"
                variant="primary text-white"
                onClick={handlePublishForReview}
                disabled={isProfileLocked}
              >
                <h6 className="mb-0 smallFont">{translate('publish_for_review')}</h6>
              </Button>
            )}
            <Button
              size="sm"
              variant="success text-white"
              onClick={handleSubmit}
              disabled={isProfileLocked || isUnpublisedCustomerProfileInAdminView}
            >
              <h6 className="mb-0 smallFont">{translate('save')}</h6>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerProfileForm;
